import * as React from "react";
import { Context } from "@grandvisionhq/carts-v2";
import { pushLoginExpressDeliveryOptionClickEventToDataLayer } from "../../../../data-layer.js";
import { useSetServiceStore } from "../../../../hooks/operations/hooks/operations/use-set-service-store.js";
import { useSetShippingMethod } from "../../../../hooks/operations/hooks/operations/use-set-shipping-method.js";
import { useShippingMethodsList } from "../../../../StepShipping/modules/ShippingMethods/hooks/StepShipping/modules/ShippingMethods/hooks/use-shipping-methods-list.js";
import { useLoggedInCheckout } from "../../../context/LoginExpressCheckout/context/context.js";
import { ShippingMethod } from "../../../../models/step-shipping/models/step-shipping/shipping-methods.js";
import { useCheckoutSettings } from "../../../../context/CheckoutSettings/context/CheckoutSettings/context.js";
const useShippingStateConfig = () => {
  const { cart } = Context.useCart();
  const cartShippingMethod = cart?.shippingMethod;
  const { shippingState, setShippingState, selectedShippingMethod, isUserLoggedIn } = useLoggedInCheckout();
  const { shipping } = useCheckoutSettings();
  const [selectedStore, setSelectedStore] = React.useState(() => {
    if (shippingState.store) {
      return shippingState.store;
    }
    return void 0;
  });
  const [selectedPickupPoint, setSelectedPickupPoint] = React.useState(
    () => {
      if (shippingState.pickupPoint) {
        return shippingState.pickupPoint;
      }
      return void 0;
    }
  );
  const isPickUpOption = React.useCallback(
    (option) => option === ShippingMethod.Pickup || option === ShippingMethod.PickupPoint,
    []
  );
  const { setServiceStore } = useSetServiceStore();
  const onShippingMethodsLoadCompleted = React.useCallback(
    (shippingMethodList2) => {
      const isSelectedShippingMethodValid = shippingMethodList2.find(
        (method) => selectedShippingMethod.method == method.key
      );
      if (selectedShippingMethod.method && !isSelectedShippingMethodValid) {
        setShippingState({
          ...shippingState,
          selectedShippingMethod: {
            method: void 0,
            isValid: false,
            isTouched: true
          }
        });
      } else if (!selectedShippingMethod.method && shipping?.explicitShippingMethod) {
        setShippingState({
          ...shippingState,
          selectedShippingMethod: {
            method: void 0,
            isValid: false,
            isTouched: false
          }
        });
      } else if (cartShippingMethod && cartShippingMethod.key !== selectedShippingMethod.method) {
        if (cartShippingMethod.key) {
          setShippingState({
            ...shippingState,
            selectedShippingMethod: {
              method: cartShippingMethod.key,
              isValid: true,
              isTouched: false
            },
            isPickupOrder: isPickUpOption(cartShippingMethod.key)
          });
        } else {
          const defaultShippingMethod = shippingMethodList2.find((method) => method.isDefault);
          if (defaultShippingMethod) {
            setShippingState({
              ...shippingState,
              selectedShippingMethod: {
                method: defaultShippingMethod.key,
                isValid: true,
                isTouched: false
              },
              isPickupOrder: isPickUpOption(defaultShippingMethod.key)
            });
          }
        }
      }
    },
    [
      cartShippingMethod,
      isPickUpOption,
      selectedShippingMethod.method,
      setShippingState,
      shipping?.explicitShippingMethod,
      shippingState
    ]
  );
  const {
    shippingMethodList,
    reloadShippingMethods,
    isLoading: shippingOptionsLoading,
    error: shippingOptionsError
  } = useShippingMethodsList(onShippingMethodsLoadCompleted);
  const onShippingMethodError = React.useCallback(() => {
    reloadShippingMethods();
  }, [reloadShippingMethods]);
  const { setShippingMethod } = useSetShippingMethod({
    onError: onShippingMethodError
  });
  const validateStoreSelection = React.useCallback(async () => {
    if (selectedStore?.globalStoreId !== cart?.serviceStore?.globalStoreId) {
      selectedStore?.globalStoreId && await setServiceStore(selectedStore?.globalStoreId);
    }
  }, [cart?.serviceStore?.globalStoreId, selectedStore?.globalStoreId, setServiceStore]);
  const getLocationId = React.useCallback(
    (shippingOption) => {
      switch (shippingOption) {
        case ShippingMethod.Pickup:
          return selectedStore?.globalStoreId;
        case ShippingMethod.PickupPoint:
          return selectedPickupPoint?.globalPickupPointId;
        default:
          return void 0;
      }
    },
    [selectedStore?.globalStoreId, selectedPickupPoint?.globalPickupPointId]
  );
  const handleShippingOptionSelection = React.useCallback(
    async (shippingOption) => {
      if (!shippingMethodList || shippingMethodList.length < 0) {
        throw new Error("No shipping methods available!");
      }
      const shippingMethod = shippingMethodList.find((item) => item.key === shippingOption);
      const isPickupOption = isPickUpOption(shippingOption);
      const isValidPickUpInStore = shippingOption === ShippingMethod.Pickup && selectedStore;
      if (!shippingMethod) {
        console.warn("Partially unsupported shipping method selected, attempting to select it");
      } else {
        const locationId = getLocationId(shippingOption);
        await setShippingMethod(
          shippingMethod?.id,
          isValidPickUpInStore && locationId ? locationId : void 0
        );
        setShippingState({
          ...shippingState,
          selectedShippingMethod: {
            method: shippingOption,
            isValid: true,
            isTouched: true
          },
          isPickupOrder: isPickupOption
        });
        if (isValidPickUpInStore) {
          validateStoreSelection();
        }
        pushLoginExpressDeliveryOptionClickEventToDataLayer({
          shippingMethod: shippingOption,
          isLoggedIn: isUserLoggedIn
        });
      }
    },
    [
      shippingMethodList,
      isPickUpOption,
      selectedStore,
      getLocationId,
      setShippingState,
      shippingState,
      isUserLoggedIn,
      setShippingMethod,
      validateStoreSelection
    ]
  );
  const onPickUpPointOrStoreModelClosed = React.useCallback(() => {
    if (!shippingMethodList || shippingMethodList.length < 0 || !cartShippingMethod) {
      throw new Error("No shipping methods available!");
    }
    if (selectedShippingMethod.method === ShippingMethod.Pickup && !selectedStore || selectedShippingMethod.method === ShippingMethod.PickupPoint && !selectedPickupPoint) {
      setShippingState({
        ...shippingState,
        isPickupOrder: isPickUpOption(cartShippingMethod?.key),
        selectedShippingMethod: {
          ...shippingState.selectedShippingMethod,
          method: cartShippingMethod?.key
        }
      });
    }
  }, [
    cartShippingMethod,
    isPickUpOption,
    selectedPickupPoint,
    selectedShippingMethod.method,
    selectedStore,
    setShippingState,
    shippingMethodList,
    shippingState
  ]);
  const handlePickupStoreSelection = React.useCallback(
    async (store) => {
      if (!shippingMethodList || shippingMethodList.length < 0) {
        throw new Error("No shipping methods available!");
      }
      const shippingMethod = shippingMethodList.find(
        (item) => item.key === ShippingMethod.Pickup
      );
      if (!shippingMethod) {
        console.warn("Partially unsupported shipping method selected, attempting to select it");
      } else {
        await setShippingMethod(shippingMethod?.id, store.globalStoreId);
      }
      setServiceStore(store.globalStoreId);
      setSelectedStore(store);
      setShippingState({
        ...shippingState,
        selectedShippingMethod: {
          method: shippingMethod?.key,
          isValid: true,
          isTouched: true
        },
        store,
        isPickupOrder: true
      });
    },
    [shippingMethodList, setServiceStore, setShippingState, shippingState, setShippingMethod]
  );
  const handlePickupPointSelection = React.useCallback(
    async (pickupPoint) => {
      if (!shippingMethodList || shippingMethodList.length < 0) {
        throw new Error("No shipping methods available!");
      }
      const shippingMethod = shippingMethodList.find(
        (item) => item.key === ShippingMethod.PickupPoint
      );
      if (!shippingMethod) {
        console.warn("Partially unsupported shipping method selected, attempting to select it");
      } else {
        await setShippingMethod(shippingMethod?.id);
      }
      setSelectedPickupPoint(pickupPoint);
      setShippingState({
        ...shippingState,
        selectedShippingMethod: {
          method: shippingMethod?.key,
          isValid: true,
          isTouched: true
        },
        pickupPoint,
        isPickupOrder: true
      });
    },
    [setShippingMethod, setShippingState, shippingMethodList, shippingState]
  );
  React.useEffect(() => {
    if (!selectedShippingMethod.isTouched && selectedShippingMethod.isValid && shipping?.explicitShippingMethod) {
      setShippingState({
        ...shippingState,
        selectedShippingMethod: { method: void 0, isValid: false, isTouched: false }
      });
    }
  }, [selectedShippingMethod, shipping?.explicitShippingMethod]);
  return React.useMemo(
    () => ({
      shippingMethodList,
      selectedShippingMethod,
      shippingOptionsLoading,
      shippingOptionsError,
      handleShippingOptionSelection,
      selectedPickupPoint,
      selectedStore,
      onStoreSelect: handlePickupStoreSelection,
      onPickupPointSelect: handlePickupPointSelection,
      onShippingModalClose: onPickUpPointOrStoreModelClosed
    }),
    [
      shippingMethodList,
      selectedShippingMethod,
      shippingOptionsLoading,
      shippingOptionsError,
      handleShippingOptionSelection,
      selectedPickupPoint,
      selectedStore,
      handlePickupStoreSelection,
      handlePickupPointSelection,
      onPickUpPointOrStoreModelClosed
    ]
  );
};
export {
  useShippingStateConfig
};
//# sourceMappingURL=useShippingStateConfig.js.map
